/** Minimal promise wrapper over IndexedDB for the offline POS (FR-OFF-01/02). */

const DB_NAME = "zeeta-offline";
const DB_VERSION = 1;
export const STORES = { snapshots: "snapshots", outbox: "outbox" } as const;
type StoreName = (typeof STORES)[keyof typeof STORES];

let dbPromise: Promise<IDBDatabase> | undefined;

function open(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORES.snapshots)) db.createObjectStore(STORES.snapshots);
      if (!db.objectStoreNames.contains(STORES.outbox)) {
        const outbox = db.createObjectStore(STORES.outbox, { keyPath: "clientId" });
        outbox.createIndex("tenant", "tenantId");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = undefined;
      reject(req.error);
    };
  });
  return dbPromise;
}

function run<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const req = fn(tx.objectStore(store));
        // Resolve on commit, not on request success, so a write is durable before we report it.
        tx.oncomplete = () => resolve(req.result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
  );
}

export const idb = {
  get: <T>(store: StoreName, key: IDBValidKey) =>
    run<T | undefined>(store, "readonly", (s) => s.get(key)),
  put: <T>(store: StoreName, value: T, key?: IDBValidKey) =>
    run(store, "readwrite", (s) => s.put(value, key)),
  delete: (store: StoreName, key: IDBValidKey) => run(store, "readwrite", (s) => s.delete(key)),
  byIndex: <T>(store: StoreName, index: string, value: IDBValidKey) =>
    run<T[]>(store, "readonly", (s) => s.index(index).getAll(value)),
};
